import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { APIService } from "src/app/API.service";
import { TranslateService } from "@ngx-translate/core";
import {
    SpaceService,
    MissionService,
    BaseUserService,
    getSignedImageUrlForSpace,
    VisitTemplate,
    TemplateService,
    BaseLateralTabService,
    getSignedFile,
    Mission,
    Organisation,
    Space,
} from "@smarterplan/ngx-smarterplan-core";
import { DateTime, Interval } from "luxon";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { GuidedTourService } from "../../guided-tour.service";
import { AudioSettingsComponent } from "../modals/audio-settings/audio-settings.component";

@Component({
    selector: "app-waiting-room",
    templateUrl: "./waiting-room.component.html",
    styleUrls: ["./waiting-room.component.scss"],
})
export class WaitingRoomComponent implements OnInit {
    currentMission: Mission;

    pseudo: string;

    templateID: string;

    spaceID: string;

    space: Space;

    visitTemplate: VisitTemplate;

    museum: Organisation;

    summary = "";

    description = "";

    timeBeforeStart: any;

    timeSinceStart: any;

    launchSubscription: any;

    countdownInterval: any;

    btnLabel = "";

    connecting = false;

    signedLogo: string;

    constructor(
        private route: ActivatedRoute,
        private spaceService: SpaceService,
        private missionService: MissionService,
        private userService: BaseUserService,
        private router: Router,
        public API: APIService,
        private guidedTourService: GuidedTourService,
        private translate: TranslateService,
        private templateService: TemplateService,
        private tabService: BaseLateralTabService,
        private modalService: NgbModal,
    ) { }

    ngOnInit(): void {
        this.route.params.subscribe((parameters) => {
            this.templateID = parameters.id;
            this.currentMission = this.userService.currentMissions.find(
                (mission) => mission.templateMissionID === this.templateID,
            );
            this.pseudo = this.userService.cu.displayName;
            this.spaceID = this.currentMission.spaceID;
            this.setSpace();
            this.setVisitMission();
            this.udpateJoinedState(true);
        });
    }

    async ngOnDestroy() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            // console.log("interval cleaned");
        }
        if (this.launchSubscription) {
            this.launchSubscription.unsubscribe();
        }
    }

    /**
     * When the user click on the "Audio settings" button
     */
    onOpenAudioSettings() {
        const modalReference = this.modalService.open(AudioSettingsComponent, {
            modalDialogClass: "museum-modal",
        });
    }

    async setSpace() {
        this.space = await this.spaceService.getSpace(this.spaceID);
        await getSignedImageUrlForSpace(this.space).then((url) => {
            if (url) {
                this.space.signedImageUrl = url;
            }
        });
        if (this.currentMission.orderedOrganisation) {
            this.museum = this.currentMission.orderedOrganisation;
        } else {
            if (this.space.properties.items.length === 1) {
                this.museum = this.space.properties.items[0].organisation;
                return;
            }
            this.space.properties.items.forEach((item) => {
                if (
                    item.organisationID !==
                    "6e28201f-4679-4c29-9d2d-8a325f57cee9"
                ) {
                    // not SmarterPlan
                    this.museum = item.organisation;
                }
            });
        }
        if (this.museum.logoUrl) {
            this.signedLogo = await getSignedFile(this.museum.logoUrl);
        }
    }

    async setVisitMission() {
        this.visitTemplate = await this.templateService.get(this.templateID);
        if (this.visitTemplate.description) {
            const chunks = this.visitTemplate.description.split(".");
            [this.summary] = chunks.splice(0, 1); // first sentence
            this.summary += ".";
            this.description = chunks.join(". ");
        }

        // check if the guided tour has already started
        if (this.visitTemplate.realStartedAt) {
            if (!this.visitTemplate.realEndedAt) {
                // navigate to visit
                this.btnLabel = "waiting.btn-label-join";
                this.setTimeSinceStart();
            } else {
                this.btnLabel = "waiting.btn-label-join-free";
            }
        } else {
            this.btnLabel = "waiting.btn-label-wait";
            // set countdown
            this.setTime();
            this.countdownInterval = setInterval(() => {
                this.setTime();
            }, 1000 * 60); // every minute
            // subscribe to update of template mission
            this.launchSubscription = this.API.OnUpdateVisitTemplateListener(
                this.visitTemplate.id,
            ).subscribe({
                next: async (data) => {
                    const templateUpdated = data.value.data
                        .onUpdateVisitTemplate as unknown as VisitTemplate;
                    if (templateUpdated && templateUpdated.realStartedAt) {
                        this.connecting = true;
                        this.btnLabel = "waiting.btn-label-connecting";
                        this.visitTemplate.realStartedAt =
                            templateUpdated.realStartedAt;

                        await this.udpateJoinedState(false);
                        this.connecting = false;
                        this.guidedTourService.tourToBeLaunchedDirectly =
                            this.visitTemplate;
                        this.tabService.storeTabHistory("live");
                        this.router.navigate([`museum/${this.spaceID}`]);
                    }
                },
                error: (error) => console.error(error),
            });
        }
    }

    setTime() {
        const now = DateTime.local().setLocale("fr");
        const start = DateTime.fromISO(
            this.visitTemplate.startDateTime,
        ).setLocale("fr");
        if (now >= start) {
            this.timeBeforeStart = null;
            clearInterval(this.countdownInterval);
        } else {
            const duration = Interval.fromDateTimes(now, start)
                .toDuration(["hours", "minutes", "seconds"])
                .toObject();
            if (duration.hours > 0) {
                this.timeBeforeStart += `${duration.hours}h `;
            }
            this.timeBeforeStart = duration;
        }
    }

    setTimeSinceStart() {
        const now = DateTime.local().setLocale("fr");
        const started = DateTime.fromMillis(this.visitTemplate.realStartedAt);
        const duration = Interval.fromDateTimes(started, now)
            .toDuration(["minutes", "seconds"])
            .toObject();
        this.timeSinceStart = duration.minutes;
    }

    async onSignOut() {
        await this.udpateJoinedState(false);
        await this.userService.logoutCurrentAndSignOutAuth();
        this.router.navigate(["connect"]);
    }

    async onJoinClick() {
        await this.udpateJoinedState(false);
        this.connecting = false;
        if (!this.visitTemplate.realEndedAt) {
            this.tabService.storeTabHistory("live");
            this.connecting = true;
            this.guidedTourService.tourToBeLaunchedDirectly =
                this.visitTemplate;
            this.router.navigate([`museum/${this.spaceID}`]);
        } else {
            this.tabService.storeTabHistory("collection");
            this.router.navigate(["museum/", this.spaceID]);
        }
    }

    async udpateJoinedState(isJoined: boolean) {
        await this.missionService.update({
            id: this.currentMission.id,
            isInWaitingRoom: isJoined,
        });
    }
}
